import { ModalSize } from '@srm/ui/types/ModalSize';

export interface ITableDemand {
    offers: IOffer[];
    data: ITableDemandItem[];
    settings: ITableDemandSettings;
}

export type RatingScale = 'simtum' | 'pirsmr' | 'pstz' | 'tmcws' | '';

export type CritType = 'score' | 'info' | 'answer' | null;

export interface ITableDemandItem {
    critId: string;
    category: string;
    critName: string;
    critInfo: ITableDemandCritInfo[];
    critWeight: number | null;
    isInfoCrit: boolean;
    ratingScale: RatingScale;
    critType: CritType;
    answerType: string | null;
    answerReadonly: boolean;
    commentRequiredPredicate: string | null;
    offers: Record<string, IOfferCrit>;
}

export interface ITableDemandCritInfo {
    key: string;
    title: string;
    value: string;
}

export interface IOfferCrit {
    answer: string;
    answerSelect: string;
    description: string;
    score: number | null;
    rating: number | null;
    allowEdit: boolean;
    allowGroupEdit: boolean;
    applyToAllSupplierOffers?: boolean;
    applyToAllLotOffers?: boolean;
}

export interface IOffer {
    key: string;
    type: string;
    name: string;
    number: string;
    rank: string;
    result: string;
    acceptanceResult: number;
    acceptanceComment: string;
    approveToStageTwo: number | null;
    price: number | null;
    /** Рублёвая цена для СРАВНЕНИЯ (ранжирование, «лучшая цена»). Бэк берёт её из
     *  последнего поля PriceFields («Цена_предложения» в рублях). Сравнение цен
     *  ведётся по ней независимо от валюты предложения; price — только для показа.
     *  Для таблиц с единственным PriceField совпадает с price. */
    comparePrice: number | null;
    currencyCode: string;
    currencyText: string;
    summaryScore: number | null;
    isWithdrawn: boolean;
    isInBlackList: boolean;
    /** Заключение СБ. Сырая строка из реквизита предложения:
     *  "1" — Допущен, "0" — Не допущен, "2" — Требуется дополнительная информация.
     *  null/пусто — иконка не показывается. */
    securityConclusion: string | null;
    /** Предложенное участником количество по текущей позиции (из подтаблицы
     *  «Позиции» предложения). null — нет данных, иконка недо-/перепоставки
     *  не показывается. */
    quantity: number | null;
    /** Требуемое количество по позиции — база для сравнения с quantity. */
    positionQuantity: number | null;
    /** Единица измерения (наименование ОКЕИ) — для подписи в тултипе. */
    unit: string | null;
    /** Значения необязательных полей сравнения по ключам из settings.comparisonFields. */
    comparisonFields: Record<string, IOfferComparisonFieldValue>;
    actions: IOfferAction[];
}

export interface IOfferComparisonFieldValue {
    displayValue: number | null;
    compareValue: number | null;
    textValue: string | null;
    targetValue: number | null;
    unit: string | null;
}

export interface IOfferAction {
    key: string;
    displayName: string;
    title: string;
    modalSize: ModalSize;
    type: keyof typeof OfferActionTypes;
    visible: boolean;
    /** «Скачать документацию» — фоновой задачей (ServerOptions.runInBackground действия); нет поля — сразу файлом. */
    runInBackground?: boolean;
}

export type OfferAnswerType = 'text' | 'select' | 'date' | 'datetime' | 'money' | 'integer' | 'yesno';

export interface IOfferAnswerSetting {
    key: string;
    type: OfferAnswerType;
    allowCustomAnswer: boolean;
    answers: IOfferAnswerItem[];
}

export interface IOfferAnswerItem {
    value: string;
    commentRequired: boolean;
}

export type TotalWinnerFactorType = 'summaryScore' | 'rank';

export type ComparisonFieldFormat = 'number' | 'money' | 'text';
export type ComparisonFieldRule = 'none' | 'min' | 'max' | 'target';
export type ComparisonFieldDelta = 'absolute' | 'percent';

export interface IComparisonFieldSetting {
    key: string;
    title: string;
    format: ComparisonFieldFormat;
    comparison: ComparisonFieldRule;
    delta: ComparisonFieldDelta;
    precision: number;
    defaultVisible: boolean;
    hint: string | null;
}

export interface ITableDemandSettings {
    emptyTextHeader: string;
    emptyText: string;
    offerCritDisabledHint: string;
    offerUnAcceptableHint: string;
    categorySortOrder: string[];
    priceVisible: boolean;
    editOfferCritButtonName: string | null;
    publicMode: boolean;
    hideSummary: boolean;
    showAnswerInScore: boolean;
    lowCostPurchase: boolean;
    ratingScaleHints: Record<string, string> | null;
    customScaleCrits: string[] | null;
    offerAnswerSettings: IOfferAnswerSetting[] | null;
    totalWinnerFactor: TotalWinnerFactorType;
    blackListHint: string | null;
    /** Доступные дополнительные поля. Пустой список полностью скрывает селектор. */
    comparisonFields: IComparisonFieldSetting[];
    comparisonFieldsMaxSelected: number;
}

export enum OfferActionTypes {
    /// <summary>
    /// Открыть предложение
    /// </summary>
    OpenOffer,
    /// <summary>
    /// Скачать документацию
    /// </summary>
    DownloadAttachments,
    /// <summary>
    /// Оценить
    /// </summary>
    RateOffer,
    /// <summary>
    /// Технический анализ
    /// </summary>
    TechAnalysisOffer,
    /// <summary>
    /// Отображение кастомной формы документа
    /// </summary>
    CustomForm,
    /// <summary>
    /// Допустить ко второму этапу
    /// </summary>
    ApproveToStageTwo,
    /// <summary>
    /// Отправить на переоценку
    /// </summary>
    Revaluate,
    /// <summary>
    /// Оценить ПКО
    /// </summary>
    RateOfferPKO,
}
